import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NotificationsService } from 'angular2-notifications';
import { FormBuilder, Validators } from '@angular/forms';
import { UserService } from '../../services/user.service';
import { OfframpService } from '../../services/offramp.service';
import { Ng4LoadingSpinnerService } from 'ng4-loading-spinner';

const defaults = {
    notif: {
        timeOut: 3000,
        showProgressBar: true,
        pauseOnHover: false,
        clickToClose: true,
        maxLength: 50
    }
}

@Component({
    selector: 'app-add-edit-payout-order',
    templateUrl: './add-edit-payout-order.component.html',
    styleUrls: ['./add-edit-payout-order.component.css']
})
export class AddEditPayoutOrderDialog implements OnInit {
    element: any = {};
    myForm: any;
    customerList: any[] = [];
    filteredCustomerList: any[] = [];
    userSearchValue: string = '';
    isUserSelectOpen: boolean = false;
    isEditMode: boolean = false;
    recordId: string = '';
    private isCalculating: boolean = false; // Flag to prevent calculation loops
    
    constructor(
        public dialogRef: MatDialogRef<AddEditPayoutOrderDialog>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private notifSer: NotificationsService,
        public fb: FormBuilder,
        public userService: UserService,
        public offrampService: OfframpService,
        private spinnerService: Ng4LoadingSpinnerService
    ) {
        this.element = data.record || {};
        this.isEditMode = !!this.element._id;
        this.recordId = this.element._id || '';
        
        // Never auto-generate any values - keep all fields empty if not present
        const PaymentOrderId = this.element.PaymentOrderId || '';
        const paymentRefId = this.element.PaymentRefId || '';
        const paymentTxnId = this.element.PaymentTxnId || '';
        const clientOrderId = this.element.ClientOrderId || '';
        const quoteId = this.element.QuoteId || '';
        const txnHash = this.element.TxnHash || '';
        const txnId = this.element.TxnId || '';
        
        this.myForm = this.fb.group({
            UserId: [this.element.UserId ? (this.element.UserId._id || this.element.UserId) : '', [Validators.required]],
            OrderStatus: [this.element.OrderStatus || 'PROCESSING', [Validators.required]],
            PaymentStatus: [this.element.PaymentStatus || 'PENDING', [Validators.required]],
            TxnStatus: [this.element.TxnStatus || 'PENDING', [Validators.required]],
            CryptoCurrency: [this.element.CryptoCurrency || 'USDT', [Validators.required]],
            CryptoAmount: [this.element.CryptoAmount || 0, [Validators.required, Validators.min(0)]],
            FiatCurrency: [this.element.FiatCurrency || 'INR', [Validators.required]],
            FiatAmount: [this.element.FiatAmount || 0, [Validators.required, Validators.min(0)]],
            PayoutAmount: [this.element.PayoutAmount || 0, [Validators.required, Validators.min(0)]],
            Rate: [this.element.Rate || this.element.FixedRate || 0, [Validators.min(0)]],
            TdsCharged: [this.element.TdsCharged || 0, [Validators.min(0)]],
            PaymentRefId: [paymentRefId],
            PaymentTxnId: [paymentTxnId],
            PaymentOrderId: [PaymentOrderId],
            ClientOrderId: [clientOrderId],
            QuoteId: [quoteId],
            Note: [this.element.Note || ''],
            Network: [this.element.Network || ''],
            TxnHash: [txnHash],
            TxnId: [txnId],
            ReversalStatus: [this.element.ReversalStatus || ''],
            TriggerCallback: [this.element.TriggerCallback || false],
        });
    }


    ngOnInit() {
        this.loadCustomerList();
    }

    loadCustomerList() {
        this.spinnerService.show();
        const filter = {
            _csrf: this.data._csrf,
            perpage: 10000,
            page: 1,
            search: '',
            status: 'Active'
        };
        
        this.offrampService.getOfframpCustomerList(filter).subscribe(
            (response: any) => {
                this.spinnerService.hide();
                if (response && response.customers && Array.isArray(response.customers)) {
                    this.customerList = response.customers;
                } else if (response && Array.isArray(response)) {
                    this.customerList = response;
                } else if (response && response.data && Array.isArray(response.data)) {
                    this.customerList = response.data;
                } else {
                    this.customerList = [];
                    this.notifSer.warn('Warning', 'No customers found', defaults.notif);
                }
                
                this.filteredCustomerList = this.customerList;
                
                // Select first user by default if no user is already selected (only for new records)
                if (!this.isEditMode && this.customerList.length > 0 && !this.myForm.get('UserId').value) {
                    this.myForm.patchValue({
                        UserId: this.customerList[0]._id
                    });
                }
                
                // Initialize calculations if values exist
                this.initializeCalculations();
            },
            (error) => {
                this.spinnerService.hide();
                this.notifSer.error('Error', 'Failed to load customer list', defaults.notif);
                this.customerList = [];
                this.filteredCustomerList = [];
            }
        );
    }

    onUserSelectOpened(isOpen: boolean) {
        this.isUserSelectOpen = isOpen;
        if (isOpen) {
            // Reset filter when opening
            this.userSearchValue = '';
            this.filteredCustomerList = this.customerList;
            // Wait for panel to render, then add search input
            setTimeout(() => {
                this.addSearchToSelectPanel();
            }, 150);
        } else {
            this.userSearchValue = '';
            this.filteredCustomerList = this.customerList;
        }
    }

    addSearchToSelectPanel() {
        const panel = document.querySelector('.user-select-panel-dialog');
        if (panel) {
            // Remove existing search if any
            const existingSearch = panel.querySelector('.user-search-container');
            if (existingSearch) {
                existingSearch.remove();
            }
            
            const searchContainer = document.createElement('div');
            searchContainer.className = 'user-search-container';
            searchContainer.innerHTML = `
                <input 
                    type="text" 
                    placeholder="Search user..." 
                    class="user-search-input"
                    value="${this.userSearchValue || ''}">
            `;
            
            const searchInput = searchContainer.querySelector('.user-search-input') as HTMLInputElement;
            if (searchInput) {
                searchInput.addEventListener('input', (e: any) => {
                    this.userSearchValue = e.target.value;
                    this.filterUsers(e);
                });
                searchInput.addEventListener('click', (e: any) => {
                    e.stopPropagation();
                });
                searchInput.addEventListener('keydown', (e: any) => {
                    e.stopPropagation();
                });
            }
            
            // Insert search at the top of the panel
            if (panel.firstChild) {
                panel.insertBefore(searchContainer, panel.firstChild);
            } else {
                panel.appendChild(searchContainer);
            }
            
            // Focus the search input
            if (searchInput) {
                setTimeout(() => {
                    searchInput.focus();
                }, 50);
            }
        }
    }

    filterUsers(event: any) {
        const searchTerm = event ? (event.target ? event.target.value : event) : this.userSearchValue;
        this.userSearchValue = searchTerm || '';
        
        if (!searchTerm || searchTerm.trim() === '') {
            this.filteredCustomerList = this.customerList;
            return;
        }
        
        const term = searchTerm.toLowerCase();
        this.filteredCustomerList = this.customerList.filter(customer => {
            const name = (customer.name || '').toLowerCase();
            const email = (customer.email || '').toLowerCase();
            return name.includes(term) || email.includes(term);
        });
    }

    // Calculation methods
    initializeCalculations() {
        // Calculate payout amount on initialization
        this.calculatePayoutAmount();
    }

    onRateChange() {
        if (this.isCalculating) return;
        this.isCalculating = true;
        
        const rate = parseFloat(this.myForm.get('Rate').value) || 0;
        const cryptoAmount = parseFloat(this.myForm.get('CryptoAmount').value) || 0;
        const fiatAmount = parseFloat(this.myForm.get('FiatAmount').value) || 0;
        
        // Priority: If CryptoAmount exists, calculate FiatAmount
        // Otherwise, if FiatAmount exists, calculate CryptoAmount
        if (cryptoAmount > 0 && rate > 0) {
            const calculatedFiat = cryptoAmount * rate;
            this.myForm.patchValue({ FiatAmount: parseFloat(calculatedFiat.toFixed(2)) }, { emitEvent: false });
        } else if (fiatAmount > 0 && rate > 0) {
            const calculatedCrypto = fiatAmount / rate;
            this.myForm.patchValue({ CryptoAmount: parseFloat(calculatedCrypto.toFixed(2)) }, { emitEvent: false });
        }
        
        // Always recalculate payout amount when rate changes
        this.calculatePayoutAmount();
        this.isCalculating = false;
    }

    onCryptoAmountChange() {
        if (this.isCalculating) return;
        this.isCalculating = true;
        
        const rate = parseFloat(this.myForm.get('Rate').value) || 0;
        const cryptoAmount = parseFloat(this.myForm.get('CryptoAmount').value) || 0;
        
        // If CryptoAmount and Rate are available, calculate FiatAmount
        if (cryptoAmount > 0 && rate > 0) {
            const calculatedFiat = cryptoAmount * rate;
            this.myForm.patchValue({ FiatAmount: parseFloat(calculatedFiat.toFixed(2)) }, { emitEvent: false });
        }
        
        // Always recalculate payout amount when crypto amount changes
        this.calculatePayoutAmount();
        this.isCalculating = false;
    }

    onFiatAmountChange() {
        if (this.isCalculating) return;
        this.isCalculating = true;
        
        const rate = parseFloat(this.myForm.get('Rate').value) || 0;
        const fiatAmount = parseFloat(this.myForm.get('FiatAmount').value) || 0;
        
        // If FiatAmount and Rate are available, calculate CryptoAmount
        if (fiatAmount > 0 && rate > 0) {
            const calculatedCrypto = fiatAmount / rate;
            this.myForm.patchValue({ CryptoAmount: parseFloat(calculatedCrypto.toFixed(2)) }, { emitEvent: false });
        }
        
        // Always recalculate payout amount when fiat amount changes
        this.calculatePayoutAmount();
        this.isCalculating = false;
    }

    calculatePayoutAmount() {
        // Get values - handle empty strings and null values
        const fiatAmountValue = this.myForm.get('FiatAmount').value;
        const tdsChargedValue = this.myForm.get('TdsCharged').value;
        
        const fiatAmount = fiatAmountValue !== null && fiatAmountValue !== undefined && fiatAmountValue !== '' 
            ? parseFloat(fiatAmountValue) : 0;
        const tdsCharged = tdsChargedValue !== null && tdsChargedValue !== undefined && tdsChargedValue !== '' 
            ? parseFloat(tdsChargedValue) : 0;
        
        // Calculate payout amount: FiatAmount - TdsCharged (works even when TDS is 0)
        const payoutAmount = Math.max(0, fiatAmount - tdsCharged);
        
        // Always update payout amount, even if TDS is 0
        this.myForm.patchValue({ PayoutAmount: parseFloat(payoutAmount.toFixed(2)) }, { emitEvent: false });
    }

    // Save Form to database
    savePayoutOrder() {
        const { notif } = defaults;
        
        if (this.myForm.invalid) {
            this.notifSer.error('Error', 'Please fill all required fields', notif);
            return;
        }

        this.spinnerService.show();
        
        const formData = {
            ...this.myForm.value,
            _csrf: this.data._csrf
        };

        if (this.isEditMode) {
            // Update existing record
            formData._id = this.recordId;
            this.userService.updatePayoutOrder(formData).subscribe(
                (response) => {
                    this.spinnerService.hide();
                    if (response.status || response.success) {
                        this.notifSer.success('Success', response.message || 'Payout order updated successfully', notif);
                        this.dialogRef.close({ success: true, data: response });
                    } else {
                        this.notifSer.error('Error', response.message || 'Failed to update payout order', notif);
                    }
                },
                (err) => {
                    this.spinnerService.hide();
                    this.notifSer.error('Error', (err.error && err.error.message) || 'Failed to update payout order', notif);
                }
            );
        } else {
            // Create new record
            this.userService.createPayoutOrder(formData).subscribe(
                (response) => {
                    this.spinnerService.hide();
                    if (response.status || response.success) {
                        this.notifSer.success('Success', response.message || 'Payout order created successfully', notif);
                        this.dialogRef.close({ success: true, data: response });
                    } else {
                        this.notifSer.error('Error', response.message || 'Failed to create payout order', notif);
                    }
                },
                (err) => {
                    this.spinnerService.hide();
                    this.notifSer.error('Error', (err.error && err.error.message) || 'Failed to create payout order', notif);
                }
            );
        }
    }

    onCancel() {
        this.dialogRef.close({ success: false });
    }
}



// WEBPACK FOOTER //
// ./src/app/payout-orders/add-edit-payout-order/add-edit-payout-order.component.ts